import { useEffect, useState } from "react"
import { api, ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanBadge, { type PlanKey } from "../account/PlanBadge"

type InstagramAccountSummary = {
  id: number
  username: string
  plan: PlanKey
}

type MemberPermission = {
  instagram_account_id: number
  permission_level: "view" | "edit" | null
}

type MemberRow = {
  organization_member_id: number
  user_name: string
  user_email: string
  role: string
  permissions: MemberPermission[]
}

type PermissionsData = {
  members: MemberRow[]
  instagram_accounts: InstagramAccountSummary[]
}

const PERMISSION_OPTIONS = [
  { value: "",     label: "権限なし", className: "permission-select--none" },
  { value: "view", label: "閲覧",      className: "permission-select--view" },
  { value: "edit", label: "編集",      className: "permission-select--edit" },
]

const PermissionsTab = () => {
  const { selectedOrganizationId } = useSelectedAccount()
  const [data, setData]       = useState<PermissionsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    const params = selectedOrganizationId !== null ? `?organization_id=${selectedOrganizationId}` : ""
    api.get<PermissionsData>(`/account_permissions${params}`)
      .then(d => { setData(d); setLoading(false) })
      .catch(() => { setError("権限情報の取得に失敗しました"); setLoading(false) })
  }, [selectedOrganizationId])

  const getPermission = (member: MemberRow, accountId: number) =>
    member.permissions.find(p => p.instagram_account_id === accountId)?.permission_level ?? null

  const handleChange = async (
    member: MemberRow,
    accountId: number,
    newLevel: string
  ) => {
    setError(null)
    try {
      await api.patch(`/account_permissions/${member.organization_member_id}`, {
        organization_id:        selectedOrganizationId,
        organization_member_id: member.organization_member_id,
        instagram_account_id:   accountId,
        permission_level:       newLevel || null,
      })
      setData(prev => {
        if (!prev) return prev
        return {
          ...prev,
          members: prev.members.map(m => {
            if (m.organization_member_id !== member.organization_member_id) return m
            const updated = m.permissions.filter(p => p.instagram_account_id !== accountId)
            if (newLevel) updated.push({ instagram_account_id: accountId, permission_level: newLevel as "view" | "edit" })
            return { ...m, permissions: updated }
          })
        }
      })
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "権限の更新に失敗しました")
    }
  }

  if (loading) return <div className="settings-card"><p className="members-loading">読み込み中...</p></div>

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <i className="bi bi-shield-lock" />
        <h2>権限設定</h2>
      </div>

      <div className="settings-card__body settings-card__body--flush">
        {error && <div className="members-alerts"><div className="settings-alert settings-alert--error">{error}</div></div>}

        {data && data.instagram_accounts.length === 0 ? (
          <p className="members-loading">Instagramアカウントが登録されていません</p>
        ) : data ? (
          <div className="members-table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>メンバー</th>
                  {data.instagram_accounts.map(account => (
                    <th key={account.id}>
                      @{account.username}
                      <div><PlanBadge plan={account.plan} /></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.members.map(member => (
                  <tr key={member.organization_member_id}>
                    <td>
                      <div className="member-name-cell">
                        <div className="member-avatar">
                          <span>{member.user_name[0]}</span>
                        </div>
                        <div>
                          <span className="member-name-cell__name">{member.user_name}</span>
                          <span className="members-table__email"> {member.user_email}</span>
                        </div>
                      </div>
                    </td>
                    {data.instagram_accounts.map(account => (
                      <td key={account.id}>
                        <select
                          value={getPermission(member, account.id) ?? ""}
                          onChange={e => handleChange(member, account.id, e.target.value)}
                          className={`permission-select ${PERMISSION_OPTIONS.find(o => o.value === (getPermission(member, account.id) ?? ""))?.className ?? ""}`}
                        >
                          {PERMISSION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default PermissionsTab
